import { useSyncExternalStore } from 'react';

/**
 * Per-device appearance preferences (font size, density, reduced motion).
 * Persisted to localStorage and applied to <html> so the whole SPA reacts:
 *   - fontSize  → html font-size; every rem-based utility scales with it
 *     (the base is 16px — see index.css), so text-sm/avatars/rem icons grow.
 *   - density   → data-density attribute; compact lowers Tailwind v4's
 *     `--spacing` base so all padding/margin/gap utilities tighten globally.
 *   - reducedMotion → a `reduce-motion` class that near-zeroes animation and
 *     transition durations (on top of prefers-reduced-motion).
 *
 * Reactive via useSyncExternalStore so the settings tab updates live.
 */

export type FontSize = 14 | 15 | 16;
export type Density = 'cozy' | 'compact';

export interface AppearancePrefs {
  fontSize: FontSize;
  density: Density;
  reducedMotion: boolean;
}

const STORAGE_KEY = 'bunker.appearance.v1';
const DEFAULTS: AppearancePrefs = { fontSize: 16, density: 'cozy', reducedMotion: false };

function load(): AppearancePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<AppearancePrefs>;
    return {
      fontSize: parsed.fontSize === 14 || parsed.fontSize === 15 ? parsed.fontSize : 16,
      density: parsed.density === 'compact' ? 'compact' : 'cozy',
      reducedMotion: parsed.reducedMotion === true,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(prefs: AppearancePrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // private mode — degrade to in-memory for this session.
  }
}

const listeners = new Set<() => void>();
let cache: AppearancePrefs | null = null;

function getSnapshot(): AppearancePrefs {
  if (!cache) cache = load();
  return cache;
}

/** Push the current prefs onto <html>. Safe to call repeatedly. */
function apply(prefs: AppearancePrefs) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.fontSize = `${prefs.fontSize}px`;
  root.dataset.density = prefs.density;
  root.classList.toggle('reduce-motion', prefs.reducedMotion);
}

function emit() {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function update(patch: Partial<AppearancePrefs>) {
  cache = { ...getSnapshot(), ...patch };
  save(cache);
  apply(cache);
  emit();
}

export function setFontSize(value: FontSize): void {
  update({ fontSize: value });
}

export function setDensity(value: Density): void {
  update({ density: value });
}

export function setReducedMotion(value: boolean): void {
  update({ reducedMotion: value });
}

/** Apply persisted prefs on app boot. Call once from the shell. */
export function initAppearance(): void {
  apply(getSnapshot());
}

/** Reactive hook — re-renders subscribers when any appearance pref changes. */
export function useAppearancePrefs(): AppearancePrefs {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Evict the in-memory cache. Exported for unit tests only. */
export function _resetAppearanceForTest(): void {
  cache = null;
}
